import { SUPABASE_URL } from '../utils/supabase/url';
/**
 * useBrowserPermissionsBootstrap
 *
 * Runs once per user session (web-only — native shell has its own bootstrap in App.tsx).
 *
 * Notification flow: previously this hook showed a branded "Quantis would like
 * to send you notifications" dialog *before* calling `Notification.requestPermission()`.
 * That meant users saw the branded prompt followed by the OS prompt — two
 * dialogs in a row asking the same question. The OS prompt already includes
 * the origin name, so the priming pass was redundant.
 *
 * Now: we wait for the user's first interaction (click / key) post-login,
 * and trigger the native OS permission prompt directly. The user gesture
 * requirement (iOS Safari especially) is satisfied by attaching the listener
 * to the document and using the gesture as the call site. No branded UI.
 *
 * Persistent storage is requested silently — no prompt needed, most browsers auto-grant.
 */

import { useEffect } from 'react';
import { NativeBridge } from '../services/NativeBridge';
import { projectId as supabaseProjectId } from '../utils/supabase/info';
import { getAuthHeaderToken } from '../utils/supabase/client';

// Bump this version whenever the bootstrap logic changes in a way that requires
// existing users to re-run it (e.g. adding sw.js for the first time).
const BOOTSTRAP_KEY = 'quantis-browser-permissions-bootstrap-v3';

// VAPID public key as base64url-encoded raw P-256 uncompressed point (65 bytes).
// Must match VAPID_PUBLIC_KEY in push_routes.tsx and the private key in kv_store.
const VAPID_PUBLIC_KEY_B64URL =
  'BHamRr1pqtmfw7SKKp7dUHF0r2eC9vEOJoecfDK5Q5HPbZqVlU7rlvcXehMqOsWTmTX3OMD6RnHBAjJwd8i4bhk';

function b64urlToUint8Array(b64url: string): Uint8Array<ArrayBuffer> {
  // Convert base64url → base64, then decode
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64.padEnd(b64.length + (4 - (b64.length % 4)) % 4, '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes as Uint8Array<ArrayBuffer>;
}

async function syncPushSubscriptionWithServer(
  subscription: PushSubscription,
  userId: string
): Promise<void> {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys) return;
  try {
    const token = await getAuthHeaderToken();
    await fetch(
      `${SUPABASE_URL}/functions/v1/make-server-a476273b/push/subscribe`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          endpoint: json.endpoint,
          keys: json.keys,
          userAgent: navigator.userAgent,
          createdAt: new Date().toISOString(),
        }),
      }
    );
  } catch (err) {
    console.warn('[BrowserPermissions] Failed to sync push subscription with server:', err);
  }
}

// Exported so settings surfaces (web push test) can reuse the exact
// register-and-sync flow the bootstrap uses. Returns the live subscription
// or null when the browser cannot subscribe.
export async function registerWebPush(userId: string): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    // Reuse an existing subscription only when it was created under the
    // CURRENT VAPID key — push services bind subscriptions to the key used at
    // subscribe time and reject sends signed with any other (FCM 403), so a
    // stale-key subscription is permanently undeliverable. Re-subscribe.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      const currentKey = b64urlToUint8Array(VAPID_PUBLIC_KEY_B64URL);
      const existingKeyBuf = existing.options?.applicationServerKey;
      const existingKey = existingKeyBuf ? new Uint8Array(existingKeyBuf) : null;
      const sameKey =
        existingKey !== null &&
        existingKey.length === currentKey.length &&
        existingKey.every((b, i) => b === currentKey[i]);
      if (sameKey) {
        await syncPushSubscriptionWithServer(existing, userId);
        return existing;
      }
      await existing.unsubscribe().catch(() => {});
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64urlToUint8Array(VAPID_PUBLIC_KEY_B64URL),
    });
    await syncPushSubscriptionWithServer(subscription, userId);
    return subscription;
  } catch (err) {
    console.warn('[BrowserPermissions] Web push registration failed:', err);
    return null;
  }
}

async function requestPersistentStorage(): Promise<void> {
  try {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      await navigator.storage.persist();
    }
  } catch {
    // Non-critical — ignore
  }
}

export function useBrowserPermissionsBootstrap(userId: string | null): void {
  useEffect(() => {
    if (!userId) return;
    // Native shell has its own permission bootstrap — skip
    if (NativeBridge.isRunningNatively()) return;
    // Already ran for this user — just make sure the service worker / push
    // subscription is still registered if permission was granted previously.
    if (localStorage.getItem(BOOTSTRAP_KEY) === '1') {
      if ('Notification' in window && Notification.permission === 'granted') {
        registerWebPush(userId);
      }
      return;
    }

    // Persistent storage is silent — no prompt.
    requestPersistentStorage();

    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') {
      // Already decided — record that bootstrap is done, register SW if granted.
      localStorage.setItem(BOOTSTRAP_KEY, '1');
      if (Notification.permission === 'granted') registerWebPush(userId);
      return;
    }

    // Trigger the OS permission prompt on the user's first interaction post-login.
    // iOS Safari (and some other browsers) require a user gesture for
    // Notification.requestPermission() — calling it on a setTimeout would silently
    // be denied. Attaching to the document via { once: true } means whatever the
    // user touches/clicks first becomes the gesture that arms the prompt.
    let cancelled = false;
    const trigger = async () => {
      if (cancelled) return;
      cancelled = true;
      document.removeEventListener('pointerdown', trigger);
      document.removeEventListener('keydown', trigger);
      localStorage.setItem(BOOTSTRAP_KEY, '1');
      try {
        const result = await Notification.requestPermission();
        if (result === 'granted') await registerWebPush(userId);
      } catch (err) {
        console.warn('[BrowserPermissions] Notification.requestPermission failed:', err);
      }
    };
    document.addEventListener('pointerdown', trigger, { once: true });
    document.addEventListener('keydown', trigger, { once: true });

    return () => {
      cancelled = true;
      document.removeEventListener('pointerdown', trigger);
      document.removeEventListener('keydown', trigger);
    };
  }, [userId]);
}
